import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { formatTimezoneAbbreviation } from 'services/UtilityService';
import {
  EnhancedLiveClassOffering,
  LiveVirtualClass,
  Session,
} from 'types/liveClass';

export type DeliveryDay = {
  start: DateTime;
  end: DateTime;
  sessionName?: string;
};

type OfferingScheduleFields = Pick<LiveVirtualClass, 'sessions' | 'timezone'>;

/**
 * Build delivery days from LMS session data only.
 * Offering start/end dates are not used — duration can differ from session count
 * (e.g. 30-day access window with weekly sessions).
 */
export function getDeliveryDays(
  offering: OfferingScheduleFields,
  timezone?: string,
): DeliveryDay[] {
  const { sessions } = offering;
  if (!Array.isArray(sessions) || sessions.length === 0) {
    return [];
  }

  const tz =
    timezone ||
    offering.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  return sessions
    .map((s: Session) => {
      const start = DateTime.fromISO(s.startDate, { zone: tz });
      const end = DateTime.fromISO(s.endDate, { zone: tz });
      return {
        start,
        end,
        sessionName: s.sessionName,
      };
    })
    .filter(({ start, end }) => start.isValid && end.isValid && !(end < start))
    .sort((a, b) => a.start.toMillis() - b.start.toMillis());
}

/** e.g. "GMT +5.5" / "GMT -5" — matches LMS schedule table mockup. */
function formatGmtOffset(offsetMinutes: number): string {
  const hours = offsetMinutes / 60;
  const sign = hours >= 0 ? '+' : '-';
  const absHours = Math.abs(hours);
  const value = Number.isInteger(absHours)
    ? String(absHours)
    : String(Math.round(absHours * 10) / 10);
  return `GMT ${sign}${value}`;
}

/**
 * Schedule table datetime: "3 Aug, 2026, 11:00 (GMT +5.5) IST"
 * (local to Live Class schedule UI — does not change shared UtilityService).
 */
export function formatScheduleDateTime(
  isoString: string,
  timezone?: string,
): string {
  if (!isoString) return '';

  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const m = DateTime.fromISO(isoString, { zone: tz });
  if (!m.isValid) return '';

  const datePart = m.toFormat('d MMM, yyyy');
  const timePart = m.toFormat('HH:mm');
  const tzPart = `(${formatGmtOffset(m.offset)}) ${formatTimezoneAbbreviation(
    m,
  )}`;
  return `${datePart}, ${timePart} ${tzPart}`;
}

/** "3 Aug, 2026, 11:00 - 19:00 (GMT +5.5) IST" */
export function formatDeliveryDayRange(
  day: DeliveryDay,
  timezone?: string,
): string {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const start = day.start.setZone(tz);
  const end = day.end.setZone(tz);
  if (!start.isValid || !end.isValid) return '';

  const datePart = start.toFormat('d MMM, yyyy');
  const startClock = start.toFormat('HH:mm');
  const endClock = end.toFormat('HH:mm');
  const tzPart = `(${formatGmtOffset(
    start.offset,
  )}) ${formatTimezoneAbbreviation(start)}`;
  return `${datePart}, ${startClock} - ${endClock} ${tzPart}`;
}

type Props = {
  offering: EnhancedLiveClassOffering;
  timezone?: string;
};

const DeliveryDaysExpand: React.FC<Props> = ({ offering, timezone }) => {
  const { t } = useTranslation();
  const days = getDeliveryDays(offering, timezone);

  if (days.length === 0) {
    return (
      <div className="live-class-delivery-days live-class-delivery-days--empty">
        {t('No delivery days available')}
      </div>
    );
  }

  return (
    <div className="live-class-delivery-days">
      <ul>
        {days.map((day, index) => {
          const label = day.sessionName || `${t('Day')} ${index + 1}`;
          const rangeLabel = formatDeliveryDayRange(day, timezone);

          return (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${day.start.toMillis()}-${index}`}>
              <span className="live-class-delivery-days__label">{label}</span>
              <span className="live-class-delivery-days__range">
                {rangeLabel}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default DeliveryDaysExpand;
